import {FC, ReactNode, useCallback, useEffect, useState} from 'react';
import {StoreContext, StoreContextData} from '../../contexts/storeContext';
import {createChartViewStore} from '../../store/useChartViewStore';
import {createPrimaryChartsFiltersStore} from '../../store/usePrimaryChartsFiltersStore';
import {createSecondaryChartsFiltersStore} from '../../store/useSecondaryChartsFiltersStore';
import {DashboardTab, useDashboardTabsStore} from '../../store/useDashboardTabsStore';
import {useDeviceTypesDataGate} from '../../hooks/useDeviceTypesDataGate';
import {createDashboardTypeStore} from '../../store/useDashboardTypeStore';
import {createRequestsPeriodFiltersStore} from '../../store/useRequestsPeriodFiltersStore';
import {createServiceRequestsStatusFiltersStore} from '../../store/useServiceRequestsStatusFilterStore';
import {createDashboardServiceRequestsFiltersStore} from '../../store/useDashboardServiceRequestsFiltersStore';
import {
  createDashboardArchiveFilterStore,
  createServiceRequestsArchiveFilterStore,
} from '../../store/useDashboardArchiveFilterStore';

interface Props {
  children: (loading: boolean, stores: StoreContextData) => ReactNode;
}

export const StoresProvider: FC<Props> = ({children}) => {
  useDeviceTypesDataGate();

  const {loading, tabs} = useDashboardTabsStore();

  const [isPreparing, setIsPreparing] = useState(true);
  const [stores, setStores] = useState<StoreContextData>({});
  const [dashboardTypeStore] = useState(() => createDashboardTypeStore());

  const prepareStores = useCallback((dt: DashboardTab[]) => {
    setIsPreparing(true);
    const result = dt.reduce<StoreContextData>((acc, tab) => {
      acc[tab.value] = {
        chartViewStore: createChartViewStore(),
        dashboardTypeStore,
        primaryChartsFiltersStore: createPrimaryChartsFiltersStore(),
        secondaryChartsFiltersStore: createSecondaryChartsFiltersStore(),
        requestsPeriodFiltersStore: createRequestsPeriodFiltersStore(),
        serviceRequestsStatusFilterStore: createServiceRequestsStatusFiltersStore(),
        dashboardServiceRequestsFiltersStore: createDashboardServiceRequestsFiltersStore(tab.value),
        dashboardArchiveFilterStore: createDashboardArchiveFilterStore(),
        serviceRequestsArchiveFilterStore: createServiceRequestsArchiveFilterStore(),
      };

      return acc;
    }, {});
    setStores(result);
    setIsPreparing(false);
  }, [dashboardTypeStore]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => prepareStores(tabs), [tabs]);

  const isLoading = loading || isPreparing;

  return <StoreContext.Provider value={stores}>{children(isLoading, stores)}</StoreContext.Provider>;
};
